import { Drawer, List } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import { PromoCodeTermsDrawerProps } from './types'
import parse from 'html-react-parser'
import { PROMO_CODE_KEY_MAPPING } from './constants'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import { PRODUCT_PROMO_CODES, PRODUCT_PROMOS } from 'components/promo-code/constants'
import { ProductPromo } from 'components/promo-code/types'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { InputGroupKeys } from 'store/customer/enums'
import { CUSTOMER_TYPES, LANGUAGES, PRODUCTS_CONFIG } from '@boltenergy-be/shared'
import { getPromoContext } from './utils'

const PromoCodeTermsDrawer = ({ code, ...props }: PromoCodeTermsDrawerProps) => {
  // Redux
  const { isCompany } = useSelectInputGroup(InputGroupKeys.PERSONAL_DATA)
  const customerType = isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential

  // i18n
  const { t } = useTranslation(['simulation', 'common'])

  // Router
  const { locale } = useRouter()

  // Constants
  const specificConditions = code ? PROMO_CODE_KEY_MAPPING[code]?.[locale === Language.FRENCH ? Language.FRENCH : Language.DUTCH] : null

  // Plenty promo specifics
  // Not included in the specific conditions mapping as it's a dynamic promo code that changes every month but the text remains the same.
  const isPlentyPromoCode = code && PRODUCT_PROMO_CODES.has(code)
  const connectedPromoConfigs: ProductPromo[] = isPlentyPromoCode ? PRODUCT_PROMOS.filter((promo) => promo.code[customerType] === code) : []
  const promoProducts = [...new Set(connectedPromoConfigs.map(({ product }) => product))]
  const regionAvailability = [...new Set(connectedPromoConfigs.flatMap(({ availableInRegion }) => availableInRegion))]
  const listFormatter = new Intl.ListFormat(locale, { type: 'conjunction' })
  const productNamesForPromo = promoProducts
    .map((product) => PRODUCTS_CONFIG[product]?.name?.[locale === Language.FRENCH ? LANGUAGES.FR : LANGUAGES.NL])
    .filter(Boolean)
  const translatedRegions = regionAvailability.map((region) => t(`common:regions.${region.toLowerCase()}`))
  const plentyPromoConditions = productNamesForPromo.length
    ? (t('terms.promo.plentyPromo.conditions', {
        returnObjects: true,
        context: getPromoContext(promoProducts, regionAvailability),
        products: listFormatter.format(productNamesForPromo),
        regions: listFormatter.format(translatedRegions)
      }) as string[])
    : null

  return (
    <Drawer {...props} title={t('terms.promo.title')}>
      {!isPlentyPromoCode && (
        <section className="flex-col gap-400">
          <p>{t('terms.promo.general.description')}</p>
          <List as="ul">
            {(t('terms.promo.general.bullets', { returnObjects: true, context: isPlentyPromoCode ? 'oneOff' : 'spread' }) as string[]).map(
              (text, idx) => (
                <li key={`general_${idx}`}>{text}</li>
              )
            )}
          </List>
        </section>
      )}

      {plentyPromoConditions && (
        <section className="flex-col gap-400">
          <List as="ul">
            {plentyPromoConditions?.map((text, idx) => (
              <li key={`plenty_${idx}`}>{text}</li>
            ))}
          </List>
        </section>
      )}

      {specificConditions && (
        <section className="flex-col gap-400">
          <p>{parse(t('terms.promo.specificConditions.description', { code }))}</p>
          <List as="ul">
            {specificConditions?.map((text, idx) => (
              <li key={`specific_${idx}`}>{text}</li>
            ))}
          </List>
        </section>
      )}
    </Drawer>
  )
}

export default PromoCodeTermsDrawer
